import { ClockClockwiseIcon } from '@phosphor-icons/react';
import { Link } from 'react-router-dom';

import { routes } from '@/config/routes';
import { formatLastSync } from '@/lib/date';

import { DEVICE_COPY, DEVICE_STATUS, type CommandType } from '../constants';
import { isCardDisabled, isPowerEnergised, powerControl } from '../device-rules';
import type { Device } from '../types';

import styles from './device-card.module.scss';
import { DevicePowerButton } from './device-power-button';
import { DeviceStatusChip } from './device-status-chip';

export type DeviceCardProps = {
  device: Device;
  /**
   * The Field this device sits in, joined from the fields API.
   *
   * `GET /devices/tracking/list` returns `field: null` on **every** row, including devices that
   * really are linked (probed 2026-09-01), so `device.fields` is always empty against the real API
   * and the chip never rendered. `<DeviceGrid>` resolves the link once for the whole page and
   * passes it down; see `useDeviceFieldLinks`. Mobile shows the same chip from the same join.
   */
  linkedFieldName?: string | null;
  onQueueCommand: (device: Device, command: CommandType) => void;
};

/**
 * One device tile in the grid (Figma node 232:355173).
 *
 * The whole tile is a link to the device details page (US-112439 §6 "Card click navigates to
 * Device Details"), except for the power button, which stops propagation. A server-disabled card
 * (`cardState: 'disabled'`, i.e. Pending QC / Pending Configuration) renders as a
 * non-interactive `<div>` instead — the AC requires it stay visible but unclickable with every
 * control disabled (§8).
 */
export function DeviceCard({ device, linkedFieldName, onQueueCommand }: DeviceCardProps) {
  const power = powerControl(device);
  const energised = isPowerEnergised(device);
  const disabled = isCardDisabled(device);
  // The payload's own `field` is preferred on the chance the backend starts populating it; the
  // joined name is what actually arrives today.
  const fieldNames = device.fields.length
    ? device.fields.map((field) => field.name)
    : linkedFieldName
      ? [linkedFieldName]
      : [];
  const lastSync = formatLastSync(device.lastSyncedAt);
  const queued = device.queuedCommand;

  const body = (
    <>
      <div className={styles.content}>
        <div className={styles.upper}>
          <div className={styles.headerRow}>
            <div className={styles.identity}>
              <p className={styles.category}>
                {device.category}
                {queued ? <span className={styles.categorySeparator}> |</span> : null}
                {queued ? (
                  <span className={styles.queuedTag}>
                    <span className={styles.queuedDot} aria-hidden />
                    {DEVICE_COPY.QUEUED_TAG[queued.type]}
                  </span>
                ) : null}
              </p>
              <p className={styles.name}>{device.name}</p>
            </div>

            {/* Omitted entirely — not blanked — when the device has never synced (§4). */}
            {lastSync ? (
              <p className={styles.lastSync}>
                <ClockClockwiseIcon size={10} aria-hidden />
                {lastSync}
              </p>
            ) : null}
          </div>

          <DeviceStatusChip device={device} />
        </div>

        {/*
          A device has at most one field: the API returns `field: {id, name} | null`. Figma draws
          three chips plus a `3+` overflow badge, which the contract cannot produce — so there is
          deliberately no overflow affordance here rather than dead code behind an impossible
          branch. Raised for BA/Design. An empty field list omits the row entirely (§2).
        */}
        {fieldNames.length > 0 ? (
          <div className={styles.fields}>
            {fieldNames.map((name) => (
              <span key={name} className={styles.fieldChip} title={name}>
                {name}
              </span>
            ))}
          </div>
        ) : null}
      </div>

      {power.kind !== 'hidden' ? (
        <DevicePowerButton
          className={styles.power}
          command={power.command}
          disabled={power.kind === 'disabled'}
          energised={energised}
          onQueue={(command) => onQueueCommand(device, command)}
        />
      ) : null}
    </>
  );

  const className = [
    styles.card,
    // The notch is only cut when there is a disc to sit in it — a device with no power control
    // would otherwise show an empty hole in its edge.
    power.kind !== 'hidden' ? styles.notched : undefined,
    // The bottom wash rides with the disc's lit state, so a running-but-queued card keeps it.
    energised ? styles.energised : undefined,
    disabled ? styles.disabledCard : undefined,
    // Expired is muted like a disabled card but stays clickable (§17).
    device.status === DEVICE_STATUS.EXPIRED ? styles.expiredCard : undefined,
  ]
    .filter(Boolean)
    .join(' ');

  if (disabled) {
    return (
      <div className={className} aria-disabled="true" data-testid="device-card">
        {body}
      </div>
    );
  }

  return (
    <Link
      to={routes.device(device.id)}
      className={className}
      data-testid="device-card"
      aria-label={device.name}
    >
      {body}
    </Link>
  );
}
